import { z } from 'zod';

import { isoDatetimeToDate } from './dates';
import { fixture } from './fixtures';

// ─── Index (list) item ───────────────────────────────────────────────────────

export const BizyUserConversation = z.object({
	conversationId: z.string(),
	pagePath: z.string().nullable(),
	messageCount: z.number().int(),
	firstUserMessage: z.string(),
	firstMessageAt: isoDatetimeToDate,
	lastMessageAt: isoDatetimeToDate,
	conversationTitle: z.string().nullable().optional(),
});

export type BizyUserConversation = z.infer<typeof BizyUserConversation>;

// ─── Detail message ──────────────────────────────────────────────────────────

export const BizyUserMessage = z.object({
	id: z.string(),
	userMessage: z.string(),
	bizyResponse: z.string(),
	createdAt: isoDatetimeToDate,
	emotion: z.string().nullable(),
	feedbackType: z.enum(['positive', 'negative']).nullable(),
	feedbackCategory: z.string().nullable(),
	feedbackText: z.string().nullable(),
	pagePath: z.string().nullable(),
});

export type BizyUserMessage = z.infer<typeof BizyUserMessage>;

// ─── Detail wrapper ──────────────────────────────────────────────────────────

export const BizyUserConversationDetail = z.object({
	conversationId: z.string(),
	pagePath: z.string().nullable(),
	messages: z.array(BizyUserMessage),
});

export type BizyUserConversationDetail = z.infer<typeof BizyUserConversationDetail>;

// ─── Fixtures ────────────────────────────────────────────────────────────────

export const bizyUserConversationFixture = (overrides: Partial<BizyUserConversation> = {}): BizyUserConversation => ({
	conversationId: fixture.id(),
	pagePath: '/admin/reports/participation',
	messageCount: fixture.int(1, 10),
	firstUserMessage: fixture.description('conversation'),
	firstMessageAt: fixture.pastDate(7),
	lastMessageAt: fixture.pastDate(1),
	conversationTitle: null,
	...overrides,
});

export const bizyUserMessageFixture = (overrides: Partial<BizyUserMessage> = {}): BizyUserMessage => ({
	id: fixture.id(),
	userMessage: fixture.description('question'),
	bizyResponse: fixture.description('answer'),
	createdAt: fixture.pastDate(1),
	emotion: 'curious',
	feedbackType: null,
	feedbackCategory: null,
	feedbackText: null,
	pagePath: '/admin/reports/participation',
	...overrides,
});

export const bizyUserConversationDetailFixture = (overrides: Partial<BizyUserConversationDetail> = {}): BizyUserConversationDetail => ({
	conversationId: fixture.id(),
	pagePath: '/admin/reports/participation',
	messages: [bizyUserMessageFixture(), bizyUserMessageFixture()],
	...overrides,
});
